import { nullish } from '@nestech/utils';
import {
  authenticatedMainComponentChildren,
  AuthenticatedMainComponentRoute,
  buildAuthenticatedRoute,
  AuthenticationRoutes,
} from '@web/authentication';
import { BaseRoutes, ExtendedRoute, mergeExtendedRoutes, SubMenuItemRoute } from '@web/base';
import {
  AdditionalMappingFn,
  CriticalIllnessApi,
  Environment,
  RetirementPensionApi,
  RetirementPensionApiV2,
  WORKFLOW_WIZARD_ACTION_PARAM,
} from '@web/service';
import { AopAokComponent } from './savings/aop-aok';
import { additionalPBUCriticalMapping } from './wizard/wizard-critical-illness-mapper';

const nonBlockingDisbursementUMRs = [
  'BPBUG015',
  'BPBUG055',
  'BPBUG441',
  'BPBUG915',
  'BPBUG969',
  'BPBUR264',
  'BPBUR265',
  'BPBUR419',
  'BPBUR810',
  'BPBUR815',
  'BPBUR850',
  'BPBUR945',
  'BPBURMPOT',
  'BPBURPOT',
];

const isNonBlockingDisbursement = (umrs: string[] | nullish): boolean => {
  if (!umrs) {
    return false;
  }
  return umrs.some(umr => nonBlockingDisbursementUMRs.includes(umr));
};

const wizardRoutes: Record<string, ExtendedRoute> = {
  // Any self-service action the backend links to renders here, without a route of its own
  workflowWizard: {
    path: `self-service/wizards/:${WORKFLOW_WIZARD_ACTION_PARAM}`,
    loadChildren: () => import('./wizard/workflow/wizard-workflow.routes').then(m => m.WizardWorkflowRoutes),
  },
  retirementPension: {
    path: 'wizard/retirement-pension',
    providers: [RetirementPensionApi],
    loadChildren: () => import('@web/pension-management').then(m => m.WizardRetirementPensionRoutesV1),
  },
  retirementPensionV2: {
    path: 'wizard/v2/retirement-pension',
    providers: [RetirementPensionApiV2],
    loadChildren: () => import('@web/pension-management').then(m => m.WizardRetirementPensionRoutesV2),
  },
  changeBeneficiaries: {
    path: 'wizard/change-beneficiaries',
    loadChildren: () => import('@web/risk/lazy').then(m => m.ChangeBeneficiariesWizardRoutesV1),
  },
  criticalIllness: {
    path: 'wizard/critical-illness',
    providers: [
      {
        provide: AdditionalMappingFn,
        useValue: additionalPBUCriticalMapping,
      },
      CriticalIllnessApi,
    ],
    loadChildren: () => import('@web/savings').then(m => m.WizardCriticalIllnessRoutes),
  },
  chronicIllness: {
    path: 'wizard/chronic-illness',
    loadChildren: () => import('./wizard/disability/wizard-disability.routes').then(m => m.WizardDisabilityRoutes),
  },
  grace: {
    path: 'wizard/grace-period',
    loadChildren: () => import('./wizard/grace-period/wizard-grace-period.routes').then(m => m.WizardGracePeriodRoutes),
  },
  grow: {
    path: 'wizard/grow-fund',
    loadChildren: () => import('@web/savings').then(m => m.WizardGrowFundRoutes),
  },
  stopGrow: {
    path: 'wizard/stop-privat-indbetalinger',
    loadChildren: () => import('@web/savings').then(m => m.WizardStopGrowFundRoutes),
  },
  pensionCheck: {
    path: 'wizard/pension-check',
    loadChildren: () => import('@web/savings').then(m => m.WizardPensionCheckRoutes),
  },
  transfer: {
    path: 'wizard/transfer',
    loadChildren: () => import('./wizard/transfer/wizard-transfer.routes').then(m => m.WizardTransferRoutes),
  },
};

const AuthenticatedMainComponentRouteWithChildren = (environment: Environment) => {
  const children = authenticatedMainComponentChildren(environment);
  return {
    ...AuthenticatedMainComponentRoute,
    mainComponentRoot: mergeExtendedRoutes(AuthenticatedMainComponentRoute.mainComponentRoot, {
      children: Object.values({
        ...children,
        home: mergeExtendedRoutes(children['home'], {
          loadChildren: () => import('./home/pbu-home-routes').then(m => m.PbuHomeRoutes),
        }),
        savings: mergeExtendedRoutes(children['savings'], {
          loadChildren: () =>
            import('@web/savings').then(m => {
              const drawdownDisbursementRoute = {
                ...m.routesToSubMenuItems.drawdownDisbursement[0],
                disabled: params =>
                  params.isAnyCoverActualized && !isNonBlockingDisbursement(params.member?.actualizedUmrs),
              } as SubMenuItemRoute;
              const defaults: Array<(typeof m.defaultRoutesToSubMenuItems)[0] | SubMenuItemRoute> =
                m.defaultRoutesToSubMenuItems.filter(r => r !== 'investmentProfile');
              const disbursementIndex = m.defaultRoutesToSubMenuItems.indexOf('drawdownDisbursement');
              const routesToInclude = [...defaults.toSpliced(disbursementIndex, 1, drawdownDisbursementRoute)];
              return m.buildSavingsRoutes(environment, ...routesToInclude, {
                path: 'aopAok',
                loadComponent: () => import('./savings/aop-aok').then(m => m.AopAokComponent),
                label: 'cas.aopAok',
                include: AopAokComponent.isActive,
              });
            }),
        }),

        selfService: mergeExtendedRoutes(children['selfService'], {
          loadChildren: () =>
            import('@web/self-service').then(async m => m.buildSelfServiceRoutes(environment, 'wizards', 'forms')),
        }),
        document: mergeExtendedRoutes(children['document'], {
          loadChildren: () =>
            import('@web/document').then(m => m.buildDocumentRoutes(environment, 'all', 'conditions')),
        }),
      }),
    }),
  };
};

/**
 * PBU-specific routes configuration
 */
export const pbuRoutes = (environment: Environment): ExtendedRoute[] => {
  const authenticatedRoute = buildAuthenticatedRoute(environment);

  return [
    ...Object.values(AuthenticationRoutes),
    ...Object.values(BaseRoutes),
    ...Object.values({
      ...authenticatedRoute,
      authenticatedRoot: mergeExtendedRoutes(authenticatedRoute.authenticatedRoot, {
        children: [
          ...Object.values(wizardRoutes),
          ...Object.values(AuthenticatedMainComponentRouteWithChildren(environment)),
        ],
      }),
    }),
  ];
};
